import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import useMediaQuery from '@mui/material/useMediaQuery';

// Routes 
import { getGroupList } from '../../../../routes/doctor/account';

// Redux
import { setDashboardSection } from '../../../../redux/features/general/navigation';
import { setGroupAccessList, setInitialRendering, setSelectedGroupAccess, setIsRefreshGroupAccessList } from '../../../../redux/features/doctor/group/groupAccess';

// Components 
import Legend from './Legend';
import GroupAccessRow from './GroupAccessRow';
import GroupAccessCard from './GroupAccessCard';
import LinearProgress from '@mui/material/LinearProgress';
import Dropdown from './Dropdown';
import Section from '../../../general/containers/Section';

// Icons
import AddCircleIcon from '@mui/icons-material/AddCircle';

const GroupAccessList: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    // --- State and Selectors ---
    const colors = useAppSelector(state => state.theme.colors);
    const { isRefreshGroupAccessList, selectedGroupAccess, groupAccessList, initialRendering } = useAppSelector(state => state.groupAccess);
    const group = useAppSelector(state => state.billing.group);
    const isMobile = useMediaQuery('(max-width:768px)');

    const [positionX, setPositionX] = useState(0);
    const [positionY, setPositionY] = useState(0);

    // --- Data Fetching ---
    const getGroupAccessList = async () => {
      const res = await getGroupList() as any;
      if (res.data.status === 200) {
        dispatch(setGroupAccessList(res.data.groupAccessList));
      }
    };

    // --- Handlers ---
    const handleSelection = (e: any, groupAccess: any) => {
      if (groupAccess.doctorId === group.mainDoctorId) {
        return;
      }
      if (selectedGroupAccess?._id === groupAccess?._id) {
        dispatch(setSelectedGroupAccess(null));
        return;
      }
      setPositionX(e.pageX);
      setPositionY(e.pageY);
      dispatch(setSelectedGroupAccess(groupAccess));
    };

    const changeSection = () => {
      dispatch(setDashboardSection('createGroupAccess'));
    };

    // --- Effects ---
    useEffect(() => {
      if (initialRendering) {
        getGroupAccessList();
        dispatch(setInitialRendering(false));
      }
    }, [initialRendering]);

    useEffect(() => {
      if (isRefreshGroupAccessList) {
        getGroupAccessList();
        dispatch(setIsRefreshGroupAccessList(false));
      }
    }, [isRefreshGroupAccessList]);

    // --- Render Functions for Desktop vs Mobile ---

    const renderDesktopView = () => (
      <>
        <Legend />
        <Box sx={{ overflow: 'auto', width: '100%', height: 'calc(100% - 70px)', marginTop: '2vh' }}>
          <List dense>
            {groupAccessList?.map((item, index) => (
              <ListItem sx={{ color: colors?.text }} onClick={(e) => handleSelection(e, item)} button key={index}>
                <GroupAccessRow item={item} />
              </ListItem>
            ))}
          </List>
        </Box>
      </>
    );

    const renderMobileView = () => (
      <Box sx={{ p: 2, width: '100%', height: '100%', overflow: 'auto' }}>
        {groupAccessList?.map((item, index) => (
          <GroupAccessCard
            key={index}
            item={item}
            onActionsClick={handleSelection}
          />
        ))}
      </Box>
    );

    const sideBarWidth = '70px';
    const navBarHeight = '5vh';

    // --- Main Return ---
    return (
      <Section 
        extraStyle={{ 
          backgroundColor: colors?.container, 
          padding: isMobile ? 0 : '0 1vw',
          // --- THE FIX IS APPLIED HERE ---
          position: 'absolute',
          top: navBarHeight,
          left: sideBarWidth,
          width: `calc(100% - ${sideBarWidth})`,
          height: `calc(100% - ${navBarHeight})`,
          boxSizing: 'border-box',
        }}
      >
        {groupAccessList?.length >= 1 && (
          isMobile ? renderMobileView() : renderDesktopView()
        )}

        {groupAccessList?.length === 0 && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
          }}>
            <LinearProgress sx={{ width: '80%', maxWidth: 500, marginBottom: 10 }} />
            <Box sx={{ color: colors?.text }}>{t('retrievingGroupAccess')}</Box>
          </Box>
        )}

        {selectedGroupAccess && <Dropdown positionX={positionX - 40} positionY={positionY - 30} />}

        <AddCircleIcon
          onClick={changeSection}
          sx={{
            width: 70,
            height: 70,
            color: 'rgb(45, 207, 196)',
            position: 'absolute',
            bottom: 30,
            right: 30,
            cursor: 'pointer',
            '&:hover': { color: 'rgb(65, 227, 216)' }
          }}
        />
      </Section>
    );
};

export default GroupAccessList;